import React, { useCallback, useRef, useState } from 'react';
import { ImageSourcePropType, StyleSheet, View } from 'react-native';
import { t } from '../../services/translations';
import { PickPhotoOrDocumentResult } from '../../services/utils';
import { ALERT_COLOR, DARK_GRAY, PRIMARY_COLOR, WHITE } from '../../styles/appColor';
import { FONT_FAMILY_DEFAULT } from '../../styles/globalStyle';
import { MediasInfos } from '../../utils/Medias';
import AppText, { Mode } from '../atomic/AppText';
import { AppModalOverlay } from '../atomic/AppModalOverlay';
import { TextFormField } from '../atomic/FormField';
import { PhotoPickButtons, PhotoPickButtonsRef } from '../atomic/PhotoPicker';
import { ContributionMedia } from '../../entities/ContributionMedia';
import { ContributionPhotoList } from '../molecules/ContributionPhotoList';
import { ContributionType } from '../../types';
import { TouchableOpacity } from 'react-native-gesture-handler';
import FontAwesome6 from 'react-native-vector-icons/FontAwesome6';

export type PhotoListFormFieldItem = Partial<ContributionMedia> & {
  file?: PickPhotoOrDocumentResult | ImageSourcePropType;
};

export interface PhotoListFormFieldProps {
  value?: PhotoListFormFieldItem[];
  allowPdf?: boolean;
  contributionType: ContributionType;
  photosGallery?: { file: ImageSourcePropType; uri: string }[];
  onChanged: (photos: PhotoListFormFieldItem[]) => void;
}

export default function PhotoListFormField({
  value,
  allowPdf,
  contributionType,
  photosGallery,
  onChanged,
}: PhotoListFormFieldProps) {
  const picksButtonsRef = useRef<PhotoPickButtonsRef>(null);
  const [lastClickedButton, setLastClickedButton] = useState<string | null>(null);
  const [showVideoPopup, setShowVideoPopup] = useState(false);
  const [invalidVideoUrl, setInvalidVideoUrl] = useState(false);
  const [videoUrl, setVideoUrl] = useState<string | null>(null);
  const noInteractions = true;

  const onVideoUrlChanged = useCallback(
    (videoUrl: string) => {
      setInvalidVideoUrl(false);

      for (const mediaName in MediasInfos) {
        const mediaInfo = MediasInfos[mediaName];
        if (mediaInfo.isMedia(videoUrl)) {
          setVideoUrl('');
          setShowVideoPopup(false);
          onChanged([...(value || []), { videoUrl: `${mediaName}:${videoUrl}` }]);
          return;
        }
      }

      setVideoUrl(videoUrl);
      setInvalidVideoUrl(true);
    },
    [value, onChanged]
  );

  return (
    <View style={styles.container}>
      <AppText style={styles.acceptedFormatsText}>{t('accepted_formats')}</AppText>
      <AppText style={styles.idealFormatText}>{`${t('ideal_format')} : 540 x 540 px`}</AppText>
      <View style={styles.photosContainer}>
        <ContributionPhotoList
          photos={value || []}
          noInteractions={noInteractions}
          contributionType={contributionType}
          mode="parallax"
          renderActions={({ index }) => (
            <TouchableOpacity
              onPress={() => value && onChanged(value.filter((_, i) => i !== index))}
              containerStyle={styles.deleteButton}
            >
              <FontAwesome6 size={16} name="trash" color={ALERT_COLOR} />
            </TouchableOpacity>
          )}
          onAddPressed={() => {
            picksButtonsRef.current?.pressButton(lastClickedButton || t('photos_documents'));
          }}
        />
      </View>
      <PhotoPickButtons
        ref={picksButtonsRef}
        modalText={t('image_bank')}
        setPhoto={(photo) => {
          if (Array.isArray(photo)) {
            onChanged([...(value || []), ...photo.map((photo) => ({ file: photo }))]);
          } else {
            onChanged([...(value || []), { file: photo }]);
          }
        }}
        extraOption={[
          {
            label: t('choose_a_video'),
            icon: 'video',
            onPress: () => setShowVideoPopup(true),
          },
        ]}
        allowPdf={allowPdf}
        multiple
        photosGallery={photosGallery}
        onButtonPressed={setLastClickedButton}
      />
      <AppModalOverlay isVisible={showVideoPopup} onBackdropPress={() => setShowVideoPopup(false)}>
        <AppText mode={Mode.BOLD} style={styles.videoModalTitle}>
          {t('insert_video_link')}
        </AppText>
        <TextFormField
          placeholder={t('link_to_youtube')}
          style={styles.videoModalTextField}
          value={videoUrl || undefined}
          onChangeText={onVideoUrlChanged}
          variant="contained"
        />
        <AppText style={styles.videoModalErrorLabel}>{invalidVideoUrl ? t('invalid_link') : ''}</AppText>
      </AppModalOverlay>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    alignItems: 'center',
    marginBottom: 20,
  },
  acceptedFormatsText: {
    fontFamily: FONT_FAMILY_DEFAULT.light,
    fontSize: 11,
    color: DARK_GRAY,
    paddingBottom: 10,
  },
  idealFormatText: {
    paddingBottom: 30,
    fontFamily: FONT_FAMILY_DEFAULT.light,
    fontSize: 11,
    color: DARK_GRAY,
  },
  photosContainer: {
    marginBottom: 10,
  },
  videoModalTitle: {
    textAlign: 'center',
    color: PRIMARY_COLOR,
  },
  videoModalTextField: {
    width: 280,
    paddingTop: 16,
  },
  videoModalErrorLabel: {
    color: ALERT_COLOR,
    fontSize: 12,
    paddingLeft: 8,
  },
  deleteButton: {
    position: 'absolute',
    top: 8,
    right: 8,
    zIndex: 10,
    width: 30,
    height: 30,
    backgroundColor: WHITE,
    borderRadius: 6,
    borderWidth: 1,
    borderColor: ALERT_COLOR,
    justifyContent: 'center',
    alignItems: 'center',
  },
});
